// Data-driven reminders — anchored on a contact's own data. New Closing is a
// ONE-TIME send 7–30 days after a sale closes (never for old imported history);
// the other two recur annually.
export const SINGLE_REMINDERS = [
  { id: 'new-closing',      label: 'New Closing',      icon: '🔑' },
  { id: 'sale-anniversary', label: 'Sale Anniversary', icon: '🏡' },
  { id: 'happy-birthday',    label: 'Happy Birthday',   icon: '🎂' },
];

// Fixed-/computed-date holiday reminders — apply to the whole contact list. Each
// carries its own calendar anchor so the engine knows when to fire it. Thanksgiving
// is the 4th Thursday of November (computed per year).
export const HOLIDAY_REMINDERS = [
  { id: 'holiday-new-year',     label: 'New Year',         icon: '🎉', anchor: { month: 0,  day: 1 } },
  { id: 'holiday-valentines',   label: "Valentine's Day",  icon: '💝', anchor: { month: 1,  day: 14 } },
  { id: 'holiday-july4',        label: 'Independence Day', icon: '🎆', anchor: { month: 6,  day: 4 } },
  { id: 'holiday-thanksgiving', label: 'Thanksgiving',     icon: '🦃', anchor: { thanksgiving: true } },
  { id: 'holiday-christmas',    label: 'Christmas',        icon: '🎄', anchor: { month: 11, day: 25 } },
];
export const HOLIDAY_IDS = HOLIDAY_REMINDERS.map(h => h.id);

// All code-defined (built-in) reminders: data-driven + holidays.
export const BUILTIN_REMINDERS = [...SINGLE_REMINDERS, ...HOLIDAY_REMINDERS];

// 4th Thursday of November for a given year → day-of-month.
export function thanksgivingDay(year) {
  const firstDow = new Date(year, 10, 1).getDay();    // 0=Sun … 4=Thu
  const firstThu = 1 + ((4 - firstDow + 7) % 7);
  return firstThu + 21;                               // +3 weeks → 4th Thursday
}

// A holiday reminder's annual (month, day) anchor for a given year (0-based month);
// null for ids that aren't holidays.
export function holidayAnchor(id, year) {
  const h = HOLIDAY_REMINDERS.find(x => x.id === id);
  if (!h) return null;
  if (h.anchor.thanksgiving) return { month: 10, day: thanksgivingDay(year) };
  return { month: h.anchor.month, day: h.anchor.day };
}

// A reminder's audience eligibility for a contact. Birthday needs a birthday, Sale
// Anniversary needs a linked sold property; holidays + custom apply to everyone.
export function isEligible(reminderId, contact, soldDate) {
  if (reminderId === 'happy-birthday')   return !!(contact && contact.birthday);
  if (reminderId === 'sale-anniversary' || reminderId === 'new-closing') return !!soldDate;
  return true;
}

// Auto-enroll defaults OFF — a reminder is auto-enrolled only when its flag is
// explicitly ON (`autoMap[id] === true`). The flag is inherited system → org → agent
// (seeded by seedTemplates) and each level can override it.
export function isAutoEnrolled(autoMap, reminderId) {
  return !!(autoMap && autoMap[reminderId] === true);
}

// The built-in scheduling anchor a reminder follows, or null:
//   • a built-in id (happy-birthday / sale-anniversary / holiday-*) anchors to itself
//   • a COPY of a built-in carries `anchor` = that built-in's id (set when duplicated),
//     so it still fires on the birthday / sold-date / holiday and defaults to Annual
//   • a plain custom reminder ("+ Add Custom Reminder" / Import) has no anchor → it is
//     interval-driven (One Time by default) and never date-anchored.
export function reminderAnchor(reminderId, customTemplates) {
  if (BUILTIN_REMINDERS.some(r => r.id === reminderId)) return reminderId;
  const c = (customTemplates || []).find(x => x && x.id === reminderId);
  return c && c.anchor ? c.anchor : null;
}
export function isAnchored(reminderId, customTemplates) {
  return !!reminderAnchor(reminderId, customTemplates);
}

// Effective enrollment for a contact+reminder: an explicit per-contact choice (made
// on the assignment screen) wins; otherwise follow the agent's auto-enroll setting.
// NOTE: eligibility (Happy Birthday needs a birthday, Sale Anniversary a sold date) is
// NOT checked here — those reminders assign like any other; the engine simply SKIPS
// them at send time when the data is missing (see buildJobs). This avoids churning
// assignments every time a contact's birthday/sold date is edited. (Extra args some
// callers still pass — contact, soldDate — are ignored.)
export function effectiveAssigned(detail, autoMap, reminderId) {
  if (detail && detail.explicit) return !!detail.assigned;
  return isAutoEnrolled(autoMap, reminderId);
}

// Materialize auto-enroll as explicit per-contact assignments for a scope, and remove
// auto-stamped assignments whose reminder is no longer auto-enrolled. Idempotent — safe
// to re-run. Call after adding contacts and after an auto-enroll toggle so:
//   • a NEW contact gets every currently auto-enrolled reminder, and
//   • turning a reminder auto-on assigns it to ALL existing (eligible) contacts, while
//   • turning it auto-off removes those auto-stamped assignments.
// Manual per-contact choices (explicit, no `auto` flag) are always preserved.
export function applyAutoEnrollAssignments(scope) {
  if (!scope) return;
  const key = (base) => `${base}__${scope}`;
  let autoMap = {}, contacts = [], assignments = {}, customTemplates = [];
  try { autoMap = JSON.parse(localStorage.getItem(key('ira_autoenroll')) || '{}'); } catch {}
  try { contacts = JSON.parse(localStorage.getItem(key('ira_contacts')) || '[]'); } catch {}
  try { assignments = JSON.parse(localStorage.getItem(key('ira_assignments')) || '{}'); } catch {}
  try { customTemplates = JSON.parse(localStorage.getItem(key('ira_custom_templates')) || '[]'); } catch {}
  if (!Array.isArray(contacts)) contacts = [];
  if (!Array.isArray(customTemplates)) customTemplates = [];
  const today = new Date().toISOString().split('T')[0];
  const fiveYears = new Date(); fiveYears.setFullYear(fiveYears.getFullYear() + 5);
  const fiveYearsStr = fiveYears.toISOString().split('T')[0];
  const autoOn = new Set(Object.keys(autoMap).filter(id => autoMap[id] === true));
  let changed = false;
  for (const c of contacts) {
    const asgn = migrateAssignment(assignments[c.id]);
    // 1) Stamp auto-enrolled reminders onto EVERY contact (Happy Birthday / Sale
    //    Anniversary included — they assign like any other reminder and the engine
    //    skips sending when the birthday / sold date is missing). Never override a
    //    manual choice (explicit assign/exclude); skip ones already assigned.
    for (const id of autoOn) {
      const cur = asgn[id];
      if (cur && typeof cur === 'object' && (cur.explicit || cur.assigned)) continue;
      // Anchored reminders (built-ins AND copies of built-ins) are annual + date-anchored;
      // PLAIN custom reminders use the cadence set on the reminder (default One Time).
      const oneTime = !isAnchored(id, customTemplates);
      const tmpl = customTemplates.find(c => c && c.id === id);
      const defaultIv = oneTime ? ((tmpl && tmpl.interval) || 'One Time') : 'Annual';
      asgn[id] = { ...(typeof cur === 'object' ? cur : {}), assigned: true, explicit: true, auto: true,
        startDate: (cur && cur.startDate) || today,
        interval: (cur && cur.interval) || defaultIv,
        ...(oneTime ? { endDate: (cur && cur.endDate) || fiveYearsStr } : {}) };
      changed = true;
    }
    // 2) Remove auto-stamped assignments whose auto-enroll is now OFF (keep manual ones).
    for (const id of Object.keys(asgn)) {
      const d = asgn[id];
      if (d && typeof d === 'object' && d.auto === true && !autoOn.has(id)) {
        delete asgn[id]; changed = true;
      }
    }
    assignments[c.id] = asgn;
  }
  if (changed) localStorage.setItem(key('ira_assignments'), JSON.stringify(assignments));
}

// Repeating-interval options for PLAIN custom reminders (from "+ Add Custom Reminder")
// and their copies. Built-ins and copies-of-built-ins are date-anchored + annual and
// never use these. Stored value `Annual` is shown as "Annually".
export const ADHOC_INTERVALS = ['One Time', 'Weekly', 'Bi-Weekly', 'Monthly', 'Annual'];
export const INTERVAL_LABELS = { 'One Time': 'One Time', 'Weekly': 'Weekly', 'Bi-Weekly': 'Bi-Weekly', 'Monthly': 'Monthly', 'Annual': 'Annually' };

export function emptySingleDetail() {
  return { assigned: false, startDate: '', endDate: '', runDate: '', interval: 'Annual' };
}

export function emptyAdHoc() {
  const todayStr = new Date().toISOString().split('T')[0];
  return { id: Date.now(), label: '', startDate: todayStr, endDate: '', runDate: '', interval: 'One Time' };
}

export function emptyAssignment() {
  return {
    'new-closing':      emptySingleDetail(),
    'sale-anniversary': emptySingleDetail(),
    'happy-birthday':    emptySingleDetail(),
  };
}

export function migrateAssignment(raw) {
  if (!raw) return emptyAssignment();
  const out = emptyAssignment();
  // Preserve built-in reminders (data-driven + holidays). Holiday entries are only
  // kept when present — absent ⇒ follow auto-enroll, so they need no default entry.
  for (const r of BUILTIN_REMINDERS) {
    const v = raw[r.id];
    if (v === true) out[r.id] = { ...emptySingleDetail(), assigned: true, explicit: true };
    else if (v && typeof v === 'object') out[r.id] = { ...emptySingleDetail(), ...v };
  }
  // Preserve custom template keys (custom__xxx)
  for (const key of Object.keys(raw)) {
    if (key.startsWith('custom__')) out[key] = raw[key];
  }
  return out;
}

export function isSingleAssigned(val) {
  if (!val) return false;
  if (val === true) return true;
  return !!val.assigned;
}

export function isExpired(endDate) {
  if (!endDate) return false;
  return new Date(endDate) < new Date();
}

export function countActiveAssignments(a) {
  if (!a) return 0;
  const m = migrateAssignment(a);
  const customCount = Object.keys(m)
    .filter(k => k.startsWith('custom__') && m[k]?.assigned && !isExpired(m[k]?.endDate)).length;
  return SINGLE_REMINDERS.filter(r => isSingleAssigned(m[r.id])).length + customCount;
}

// Effective count of reminders a contact will actually receive — built-ins +
// holidays + customs — honoring auto-enroll and per-contact overrides.
export function effectiveActiveCount(raw, autoMap, contact, soldDate, customTemplates = []) {
  const m = migrateAssignment(raw);
  let n = 0;
  for (const r of BUILTIN_REMINDERS) {
    if (effectiveAssigned(m[r.id], autoMap, r.id, contact, soldDate)) n++;
  }
  for (const c of customTemplates) {
    if (c.builtin || c.base) continue;   // built-in stamp entries are already counted above
    if (isExpired(m[c.id]?.endDate)) continue;
    if (effectiveAssigned(m[c.id], autoMap, c.id, contact, soldDate)) n++;
  }
  return n;
}

// ── Annual reminder scheduling ───────────────────────────────────────────────
// Dates are stored as 'YYYY-MM-DD' strings; parse/format in LOCAL time so the
// day never shifts due to UTC conversion.
function toYMD(d) {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

function parseYMD(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

// Next occurrence of (month, day) strictly after `from` — the reminder's next run.
// `month` is 0-based.
function nextAnniversaryFor(month, day, from) {
  const base = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  let next = new Date(base.getFullYear(), month, day);
  if (next <= base) next = new Date(base.getFullYear() + 1, month, day);
  return toYMD(next);
}

// If an assigned annual reminder anchored on (month, day) is due — an anniversary
// on/after its start date has passed and it hasn't run for that anniversary yet —
// return the anniversary date string to record as its last run; otherwise null.
function annualRunFor(detail, month, day, today) {
  if (!detail || !detail.assigned) return null;
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  let anniv = new Date(base.getFullYear(), month, day);
  if (anniv > base) anniv = new Date(base.getFullYear() - 1, month, day);
  const annivStr = toYMD(anniv);
  if (detail.startDate && annivStr < detail.startDate) return null; // anniversary predates assignment
  if (!detail.lastRunDate || detail.lastRunDate < annivStr) return annivStr;
  return null;
}

// Sale Anniversary — anchored on the contact's property sold date.
export function nextSaleAnniversary(soldDate, from = new Date()) {
  const sold = parseYMD(soldDate);
  if (!sold) return null;
  return nextAnniversaryFor(sold.getMonth(), sold.getDate(), from);
}

export function computeSaleAnnivRun(detail, today = new Date()) {
  if (!detail || !detail.soldDate) return null;
  const sold = parseYMD(detail.soldDate);
  if (!sold) return null;
  return annualRunFor(detail, sold.getMonth(), sold.getDate(), today);
}

// Happy Birthday — anchored on the contact's birthday (month/day).
export function nextBirthday(birthday, from = new Date()) {
  const b = parseYMD(birthday);
  if (!b) return null;
  return nextAnniversaryFor(b.getMonth(), b.getDate(), from);
}

// The sold date for a contact's Sale Anniversary, taken from the most recent
// linked property (properties carry linkedContacts + soldDate).
export function soldDateForContact(contactId, properties) {
  if (!Array.isArray(properties)) return '';
  const dates = properties
    .filter(p => p.soldDate && Array.isArray(p.linkedContacts)
      && p.linkedContacts.some(lc => String(lc.id) === String(contactId)))
    .map(p => p.soldDate)
    .sort();
  return dates.length ? dates[dates.length - 1] : '';   // most recent sale
}

// Whether a custom reminder is due based on its saved interval and last run.
// Never run → due. After running once: One Time never fires again;
// Weekly/Monthly/Annual fire only once that span has elapsed since the last send.
export function customIntervalDue(lastRunAt, interval, today = new Date()) {
  if (!lastRunAt) return true;
  const next = new Date(lastRunAt);
  if (interval === 'Weekly')       { next.setDate(next.getDate() + 7); return today >= next; }
  if (interval === 'Bi-Weekly')    { next.setDate(next.getDate() + 14); return today >= next; }
  if (interval === 'Monthly')      { next.setMonth(next.getMonth() + 1); return today >= next; }
  if (interval === 'Annual')       { next.setFullYear(next.getFullYear() + 1); return today >= next; }
  return false;   // One Time (or default) — already ran once, never again
}

// True if this year's anniversary (month/day) has been reached — today is on or
// after it. `month` is 0-based. Used to gate annual reminders so they fire on the
// anniversary date, not before.
export function anniversaryReached(month, day, today = new Date()) {
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const thisYear = new Date(base.getFullYear(), month, day);
  return base >= thisYear;
}

// Grace window (days) for date-anchored reminders: send on the anchor date and for a
// few days after (survives a brief process outage), but never a months-late catch-up —
// this stops e.g. a "Happy New Year" going out in July.
export const DUE_WINDOW_DAYS = 3;

// Whole days from the most recent anchor (on/before today) to today; -1 if not reached.
export function daysSinceAnchor(month, day, today = new Date()) {
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  let anniv = new Date(base.getFullYear(), month, day);
  if (anniv > base) anniv = new Date(base.getFullYear() - 1, month, day);
  return Math.round((base.getTime() - anniv.getTime()) / 86400000);
}
export function withinDueWindow(month, day, today = new Date(), windowDays = DUE_WINDOW_DAYS) {
  const d = daysSinceAnchor(month, day, today);
  return d >= 0 && d <= windowDays;
}

// "New Closing" fires ONCE per sale, 7–30 days after the close date: a one-week
// quiet period (the agent is naturally in touch right at closing), then a catch
// window through day 30. Older closings — e.g. history imported months later —
// are deliberately skipped so nobody gets a stale "congratulations".
export const NEW_CLOSING_MIN_DAYS = 7;
export const NEW_CLOSING_MAX_DAYS = 30;

// Whole days from a YYYY-MM-DD date to today; null when the date is missing/bad.
export function daysSinceYMD(ymd, today = new Date()) {
  const md = ymd ? String(ymd).split('-').map(Number) : [];
  if (md.length !== 3 || !md[0] || !md[1] || !md[2]) return null;
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((base.getTime() - new Date(md[0], md[1] - 1, md[2]).getTime()) / 86400000);
}

// True if an annual reminder anchored on (month, day) was already emailed during
// the current cycle — i.e. the last actual send is on/after the most recent
// anniversary. `lastRunAt` is an ISO timestamp; `month` is 0-based.
export function alreadyRunThisYear(lastRunAt, month, day, today = new Date()) {
  if (!lastRunAt) return false;
  const base = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  let anniv = new Date(base.getFullYear(), month, day);
  if (anniv > base) anniv = new Date(base.getFullYear() - 1, month, day);
  const last = new Date(lastRunAt);
  const lastDate = new Date(last.getFullYear(), last.getMonth(), last.getDate());
  return lastDate >= anniv;
}
